// =============================================================
// FLOW (9.2b): Downtime register WRITE — ported from scripts/perpetuum-desktop-
// sections4.jsx (SecDowntimes), now reading + WRITING the REAL /api/v1.
//
// Reads the live downtimes + downtime-categories + devices. "Log downtime"
// posts to /downtimes/register (server computes duration_min + fires the
// downtime.opened webhook). The log table + by-cause bars + KPIs all recompute
// from the live data after a write (query invalidation), so a logged event is
// proven persisted (it also feeds the downtime/MTBF reports). No synthetic data.
// =============================================================

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Device } from "@perpetuum/core";
import {
  useCollection,
  useConvertDowntimeToTask,
  useDevices,
  useRegisterDowntime,
  type Row,
} from "../lib/queries.js";
import { useAuth } from "../app/AuthContext.js";
import { useChrome } from "../app/ChromeContext.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Btn, Icon, ICONS } from "../components/primitives.js";
import { EmptyState } from "../components/EmptyState.js";
import { Skeleton } from "../components/Skeleton.js";
import { BarChart, type BarChartBar } from "../components/charts.js";

/** Palette for the by-cause bars — cycles through the status tokens. */
const CAUSE_COLORS = [
  "var(--status-critical)",
  "var(--status-warning)",
  "var(--status-info)",
  "var(--status-paused)",
  "var(--status-success)",
] as const;

interface DowntimeRow extends Row {
  deviceId?: string;
  categoryId?: string;
  startedAt?: string;
  endedAt?: string | null;
  durationMin?: number | null;
  reason?: string | null;
}

// `ongoingLabel` is injected by the caller (localized t("downtimes.ongoing"))
// so this module-level formatter carries no hardcoded English.
const fmtDur = (m: number | null | undefined, ongoingLabel: string): string => {
  if (m == null) return ongoingLabel;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h ? `${h} h ` : ""}${mm || !h ? `${mm} m` : ""}`.trim();
};

export function Downtimes() {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast, confirm } = useChrome();
  const navigate = useNavigate();
  const downtimesQ = useCollection<DowntimeRow>("downtimes");
  const catsQ = useCollection<Row>("downtime-categories");
  const devicesQ = useDevices();
  const register = useRegisterDowntime();
  const convert = useConvertDowntimeToTask();
  const canConvert = can("tasks", "write");

  const downtimes = downtimesQ.data ?? [];
  const cats = catsQ.data ?? [];
  const devices = devicesQ.data ?? [];
  const deviceById = useMemo(() => new Map(devices.map((d) => [d.id, d as Device])), [devices]);
  const catById = useMemo(() => new Map(cats.map((c) => [c.id, c])), [cats]);

  const [showForm, setShowForm] = useState(false);
  const [catFilter, setCatFilter] = useState<string>("all");
  const [form, setForm] = useState<{ deviceId: string; categoryId: string; reason: string; status: "ongoing" | "resolved" }>({
    deviceId: "",
    categoryId: "",
    reason: "",
    status: "ongoing",
  });

  const rows = downtimes.filter((r) => (catFilter === "all" ? true : r.categoryId === catFilter));
  const totalMin = downtimes.reduce((s, r) => s + (r.durationMin ?? 0), 0);
  const openCount = downtimes.filter((r) => !r.endedAt && r.durationMin == null).length;

  // ── Derived KPIs (all client-side from the loaded downtimes) ───────────────
  // Availability + MTBF need an observation window and *closed* failures. We
  // derive the window from the span of the data we already loaded: earliest
  // startedAt → latest endedAt (or now if anything is still open). Failures =
  // closed downtimes (a downtime with a known duration is a completed failure).
  // If we can't derive a metric honestly, we show n/a rather than fake a number.
  const metrics = useMemo(() => {
    const starts = downtimes
      .map((r) => (r.startedAt ? Date.parse(r.startedAt) : NaN))
      .filter((n) => Number.isFinite(n));
    const closed = downtimes.filter((r) => r.endedAt && r.durationMin != null);
    const failures = closed.length;
    if (starts.length === 0 || failures === 0) {
      return { availability: null as number | null, mtbfMin: null as number | null };
    }
    const earliest = Math.min(...starts);
    const ends = downtimes
      .map((r) => (r.endedAt ? Date.parse(r.endedAt) : NaN))
      .filter((n) => Number.isFinite(n));
    const latestEnd = ends.length ? Math.max(...ends) : Date.now();
    const windowMin = Math.max(1, (latestEnd - earliest) / 60_000);
    const downMin = closed.reduce((s, r) => s + (r.durationMin ?? 0), 0);
    const upMin = Math.max(0, windowMin - downMin);
    const availability = (upMin / windowMin) * 100;
    // MTBF = operational (up) time ÷ number of failures.
    const mtbfMin = upMin / failures;
    return { availability, mtbfMin };
  }, [downtimes]);

  const byCat = cats.map((c, i) => {
    const list = downtimes.filter((r) => r.categoryId === c.id);
    return {
      id: c.id,
      name: c.name ?? "—",
      color: CAUSE_COLORS[i % CAUSE_COLORS.length] ?? CAUSE_COLORS[0],
      mins: list.reduce((s, r) => s + (r.durationMin ?? 0), 0),
      n: list.length,
    };
  });

  const causeBars: BarChartBar[] = byCat.map((c) => ({
    label: String(c.name).split(" ")[0] ?? String(c.name),
    segments: [{ value: c.mins, color: c.color, label: String(c.name) }],
  }));

  const onSubmit = async () => {
    if (!can("downtime", "write")) {
      toast(t("downtimes.noPerm"), "warning");
      return;
    }
    const now = new Date();
    const startedAt = now.toISOString();
    const endedAt = form.status === "resolved" ? now.toISOString() : undefined;
    try {
      await register.mutateAsync({
        deviceId: form.deviceId || undefined,
        categoryId: form.categoryId || undefined,
        startedAt,
        endedAt,
        reason: form.reason || undefined,
      });
      toast(t("downtimes.logged"), "success");
      setShowForm(false);
      setForm({ deviceId: "", categoryId: "", reason: "", status: "ongoing" });
    } catch {
      toast(t("login.err.network_error"), "error");
    }
  };

  // Convert a downtime (fault) into a work order: confirm → POST → toast →
  // navigate to the Work board (the new WO appears after the tasks refetch).
  // Gated on tasks:write; the server is the authority (403/404 otherwise).
  const onConvert = async (r: DowntimeRow) => {
    if (!canConvert) {
      toast(t("downtimes.convertNoPerm"), "warning");
      return;
    }
    const dev = r.deviceId ? deviceById.get(r.deviceId) : undefined;
    const ok = await confirm({
      title: t("downtimes.convertTitle"),
      body: t("downtimes.convertBody", { asset: dev?.name ?? r.reason ?? "" }),
      confirmLabel: t("downtimes.convertConfirm"),
    });
    if (!ok) return;
    try {
      const res = await convert.mutateAsync(r.id);
      toast(t("downtimes.converted", { number: res.task.number ?? "" }), "success");
      navigate(`/workboard?task=${res.task.id}`);
    } catch {
      toast(t("downtimes.convertErr"), "error");
    }
  };

  return (
    <div>
      <h2 className="d-h2">{t("downtimes.title")}</h2>
      <p className="d-sub">{t("downtimes.sub")}</p>

      <div className="kpi-strip" style={{ marginBottom: 20 }}>
        <div className="kpi">
          <div className="v" data-testid="dt-total-h">
            {(totalMin / 60).toFixed(1)}
            <span className="kpi-unit">h</span>
          </div>
          <div className="k">{t("downtimes.kpi.total")}</div>
        </div>
        <div className="kpi">
          <div className="v" data-testid="dt-count">
            {downtimes.length}
          </div>
          <div className="k">{t("downtimes.kpi.events")}</div>
        </div>
        <div className="kpi">
          <div className="v" style={{ color: openCount ? "var(--status-critical-ink)" : undefined }}>
            {openCount}
          </div>
          <div className="k">{t("downtimes.kpi.down")}</div>
        </div>
        <div className="kpi" title={metrics.availability == null ? t("downtimes.kpi.naHint") : undefined}>
          <div className="v" data-testid="dt-availability">
            {metrics.availability == null ? (
              t("downtimes.kpi.na")
            ) : (
              <>
                {metrics.availability.toFixed(1)}
                <span className="kpi-unit">%</span>
              </>
            )}
          </div>
          <div className="k">{t("downtimes.kpi.availability")}</div>
        </div>
        <div className="kpi" title={metrics.mtbfMin == null ? t("downtimes.kpi.naHint") : undefined}>
          <div className="v" data-testid="dt-mtbf">
            {metrics.mtbfMin == null ? (
              t("downtimes.kpi.na")
            ) : (
              <>
                {(metrics.mtbfMin / 60).toFixed(1)}
                <span className="kpi-unit">h</span>
              </>
            )}
          </div>
          <div className="k">{t("downtimes.kpi.mtbf")}</div>
        </div>
      </div>

      <div className="d-grid-2" style={{ gridTemplateColumns: "1.5fr 1fr" }}>
        <div className="d-card">
          <div className="d-card-hd">
            <h3>{t("downtimes.log")}</h3>
            <span style={{ flex: 1 }} />
            <div className="view-seg" style={{ marginRight: 10 }}>
              <button aria-pressed={catFilter === "all"} onClick={() => setCatFilter("all")}>
                {t("downtimes.all")}
              </button>
              {cats.map((c) => (
                <button key={c.id} aria-pressed={catFilter === c.id} onClick={() => setCatFilter(c.id)}>
                  {String(c.name ?? "").split(" ")[0]}
                </button>
              ))}
            </div>
            <Btn
              variant="stop"
              size="sm"
              data-testid="log-downtime-btn"
              disabled={!can("downtime", "write")}
              onClick={() => setShowForm(true)}
            >
              <Icon d={ICONS.alert} size={16} />
              {t("downtimes.logBtn")}
            </Btn>
          </div>
          <div className="d-card-body" style={{ padding: 0 }}>
            <table className="d-table" data-testid="downtime-table">
              <thead>
                <tr>
                  <th>{t("downtimes.col.asset")}</th>
                  <th>{t("downtimes.col.cause")}</th>
                  <th>{t("downtimes.col.started")}</th>
                  <th>{t("downtimes.col.duration")}</th>
                  <th aria-label={t("downtimes.col.actions")} />
                </tr>
              </thead>
              <tbody>
                {downtimesQ.isLoading && <Skeleton variant="table-row" count={4} cols={5} />}
                {!downtimesQ.isLoading && rows.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: 0 }}>
                      <EmptyState
                        icon={ICONS.alert}
                        title={t("downtimes.empty")}
                        hint={t("downtimes.emptyHint")}
                        action={
                          can("downtime", "write")
                            ? { label: t("downtimes.logBtn"), onClick: () => setShowForm(true) }
                            : undefined
                        }
                      />
                    </td>
                  </tr>
                )}
                {rows.map((r) => {
                  const dev = r.deviceId ? deviceById.get(r.deviceId) : undefined;
                  // Resolve categoryId → category name via the Map; a null/blank
                  // category is localized "(uncategorized)" and an unmatched id
                  // (deleted category) falls back to the same — never the raw id.
                  const cat = r.categoryId ? catById.get(r.categoryId) : undefined;
                  const catLabel = cat ? String(cat.name) : t("downtimes.uncategorized");
                  const ongoing = !r.endedAt && r.durationMin == null;
                  return (
                    <tr key={r.id} className="rowhover">
                      <td style={{ fontWeight: 600 }}>
                        {dev?.name ?? "—"}
                        {r.reason && <div className="dt-note">{r.reason}</div>}
                      </td>
                      <td><span className="dt-pill">{catLabel}</span></td>
                      <td className="mono" style={{ whiteSpace: "nowrap" }}>
                        {r.startedAt ? new Date(r.startedAt).toLocaleString() : "—"}
                      </td>
                      <td className="mono">
                        {ongoing ? (
                          <span className="dt-live">
                            <span className="dt-live-dot" />
                            {t("downtimes.ongoing")}
                          </span>
                        ) : (
                          fmtDur(r.durationMin, t("downtimes.ongoing"))
                        )}
                      </td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        {canConvert && (
                          <Btn
                            variant="secondary"
                            size="sm"
                            data-testid={`dt-convert-${r.id}`}
                            disabled={convert.isPending}
                            onClick={() => void onConvert(r)}
                          >
                            <Icon d={ICONS.plus} size={13} />
                            {t("downtimes.convert")}
                          </Btn>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="d-card">
          <div className="d-card-hd">
            <h3>{t("downtimes.byCause")}</h3>
          </div>
          <div className="d-card-body">
            {catsQ.isLoading ? (
              <Skeleton variant="card" height={180} />
            ) : byCat.length === 0 ? (
              <EmptyState compact icon={ICONS.alert} title={t("downtimes.noCats")} />
            ) : (
              <BarChart
                bars={causeBars}
                unit=" min"
                ariaLabel={`${t("downtimes.byCause")}: ${byCat.map((c) => `${String(c.name)} ${c.mins} min (${c.n})`).join(", ")}`}
                legend={byCat.map((c) => ({ label: `${String(c.name)} (${c.n})`, color: c.color }))}
              />
            )}
          </div>
        </div>
      </div>

      {showForm && (
        <div className="modal-scrim" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 520 }}>
            <div className="modal-hd">
              <div className="ic">
                <Icon d={ICONS.alert} size={20} />
              </div>
              <h3>{t("downtimes.logBtn")}</h3>
            </div>
            <div className="modal-body">
              <div className="d-field">
                <label htmlFor="dt-asset">{t("downtimes.col.asset")}</label>
                <div className="field-ctrl">
                  <select id="dt-asset" value={form.deviceId} onChange={(e) => setForm((f) => ({ ...f, deviceId: e.target.value }))}>
                    <option value="">—</option>
                    {devices.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="d-field">
                <label htmlFor="dt-cat">{t("downtimes.col.cause")}</label>
                <div className="field-ctrl">
                  <select id="dt-cat" value={form.categoryId} onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}>
                    <option value="">—</option>
                    {cats.map((c) => (
                      <option key={c.id} value={c.id}>
                        {String(c.name)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="d-field">
                <label htmlFor="dt-status">{t("downtimes.statusLabel")}</label>
                <div className="field-ctrl">
                  <select
                    id="dt-status"
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as "ongoing" | "resolved" }))}
                  >
                    <option value="ongoing">{t("downtimes.ongoing")}</option>
                    <option value="resolved">{t("downtimes.resolved")}</option>
                  </select>
                </div>
              </div>
              <div className="d-field">
                <label htmlFor="dt-reason">{t("downtimes.note")}</label>
                <div className="field-ctrl">
                  <input
                    id="dt-reason"
                    placeholder={t("downtimes.notePlaceholder")}
                    value={form.reason}
                    onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <Btn variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                {t("common.cancel")}
              </Btn>
              <Btn variant="stop" size="sm" data-testid="register-downtime-btn" disabled={register.isPending} onClick={() => void onSubmit()}>
                {t("downtimes.register")}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
